import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
  Link,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { getUrl } from '../../../config';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { parseModuleMentions } from '../../../utils/moduleMentions';

const ModalChangelog = ({ open, onClose, updates, onMarkAsRead, hideMarkAsRead = false }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [readUpdates, setReadUpdates] = React.useState(new Set());
  const [remainingUpdates, setRemainingUpdates] = React.useState([]);
  const [showHistoryList, setShowHistoryList] = React.useState(false);
  const [allPublishedUpdates, setAllPublishedUpdates] = React.useState([]);
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [selectedUpdateFromHistory, setSelectedUpdateFromHistory] = React.useState(null);

  // Réinitialiser readUpdates et remainingUpdates quand le modal s'ouvre ou que les updates changent
  React.useEffect(() => {
    if (open) {
      // Réinitialiser readUpdates à chaque ouverture du modal
      // Les updates viennent du serveur et contiennent déjà uniquement les non lues
      setReadUpdates(new Set());
      setRemainingUpdates(updates);
      setActiveStep(0);
      setShowHistoryList(false);
      setSelectedUpdateFromHistory(null);
    }
  }, [open, updates]);

  const fetchAllPublishedUpdates = async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get(getUrl() + '/api/changelogs/published', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      if (response.data.status === 'success') {
        setAllPublishedUpdates(response.data.data);
        setShowHistoryList(true);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des mises à jour publiées:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleShowHistory = () => {
    if (allPublishedUpdates.length === 0) {
      fetchAllPublishedUpdates();
    } else {
      setShowHistoryList(true);
    }
  };

  const handleSelectUpdateFromHistory = (update) => {
    setSelectedUpdateFromHistory(update);
    setShowHistoryList(false);
  };

  const handleBackToCurrent = () => {
    setSelectedUpdateFromHistory(null);
    setShowHistoryList(false);
  };

  const handleModuleClick = (href) => {
    navigate(href);
    onClose(); // Fermer le modal quand on clique sur une mention
  };

  const renderContent = (content) => {
    const parts = parseModuleMentions(content, handleModuleClick);
    return parts.map((part, index) => {
      if (part.type === 'mention' && part.href) {
        return (
          <Link
            key={index}
            component="button"
            variant="body1"
            onClick={part.onClick}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              border: 'none',
              padding: 0,
              font: 'inherit',
              fontWeight: 600,
              display: 'inline',
              '&:hover': {
                color: 'primary.dark',
                textDecoration: 'underline',
              }
            }}
          >
            {part.content}
          </Link>
        );
      }
      return <span key={index}>{part.content}</span>;
    });
  };

  const handleMarkCurrentAsRead = async () => {
    const currentUpdate = remainingUpdates[activeStep];
    if (!currentUpdate) return;

    // Vérifier si c'est la dernière mise à jour AVANT de faire quoi que ce soit
    const isLastUpdate = currentUpdateIndexInFullList === updates.length - 1;

    console.log('=== DEBUG ===');
    console.log('currentUpdate:', currentUpdate);
    console.log('activeStep:', activeStep);
    console.log('currentUpdateIndexInFullList:', currentUpdateIndexInFullList);
    console.log('updates.length:', updates.length);
    console.log('isLastUpdate:', isLastUpdate);
    console.log('remainingUpdates:', remainingUpdates);

    try {
      const apiResponse = await axios.post(
        getUrl() + `/api/changelogs/${currentUpdate.id}/mark-as-read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      console.log('API call successful, response:', apiResponse.data);

      // Si c'est la dernière mise à jour, fermer immédiatement
      if (isLastUpdate) {
        onClose();
        // Ne pas appeler onMarkAsRead ici car l'API peut retourner des données obsolètes
        // et rouvrir le modal. Le parent se mettra à jour naturellement.
        return;
      }

      // Marquer cette mise à jour comme lue
      const newReadUpdates = new Set([...readUpdates, currentUpdate.id]);
      setReadUpdates(newReadUpdates);

      // Retirer cette mise à jour de la liste des mises à jour restantes
      const newRemainingUpdates = remainingUpdates.filter(u => u.id !== currentUpdate.id);
      setRemainingUpdates(newRemainingUpdates);

      // Si plus de mises à jour restantes, fermer le modal
      if (newRemainingUpdates.length === 0) {
        // Fermer d'abord le modal pour éviter que le useEffect ne réinitialise l'état
        onClose();
        // Puis appeler onMarkAsRead pour rafraîchir les données du parent
        if (onMarkAsRead) {
          // Utiliser setTimeout pour s'assurer que le modal est fermé avant
          setTimeout(() => onMarkAsRead(), 0);
        }
        return;
      }

      // Sinon, passer à la prochaine mise à jour
      // Si on était à la dernière position, revenir à l'avant-dernière
      if (activeStep >= newRemainingUpdates.length) {
        setActiveStep(newRemainingUpdates.length - 1);
      }
      // Sinon, rester sur le même index (qui affichera maintenant la mise à jour suivante)
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      for (const update of updates) {
        if (!readUpdates.has(update.id)) {
          await axios.post(
            getUrl() + `/api/changelogs/${update.id}/mark-as-read`,
            {},
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`
              }
            }
          );
        }
      }
      if (onMarkAsRead) {
        onMarkAsRead();
      }
      onClose();
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
    }
  };

  // Utiliser remainingUpdates pour trouver la mise à jour actuelle
  // et trouver son index dans la liste complète pour le stepper
  const currentUpdate = selectedUpdateFromHistory || remainingUpdates[activeStep];
  const currentUpdateIndexInFullList = currentUpdate && !selectedUpdateFromHistory
    ? updates.findIndex(u => u.id === currentUpdate.id)
    : -1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[24],
        }
      }}
    >
      <DialogTitle
        sx={{
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 2.5,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            📢 Nouvelles Mises à Jour
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3, backgroundColor: (theme) => theme.palette.background.default }}>
        {showHistoryList ? (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Mises à jour antérieures
              </Typography>
              <Button
                onClick={() => setShowHistoryList(false)}
                variant="outlined"
                size="small"
              >
                Retour
              </Button>
            </Box>
            {loadingHistory ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {allPublishedUpdates.map((update) => (
                  <ListItem key={update.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectUpdateFromHistory(update)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <ListItemText
                        primary={update.title}
                        secondary={dayjs(update.created_at).format('DD/MM/YYYY')}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: 'primary.main'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                {allPublishedUpdates.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Aucune mise à jour antérieure disponible
                  </Typography>
                )}
              </List>
            )}
          </Box>
        ) : (
          <>
            {selectedUpdateFromHistory && (
              <Box mb={2}>
                <Button
                  onClick={handleBackToCurrent}
                  variant="outlined"
                  size="small"
                  startIcon={<HistoryIcon />}
                >
                  Retour aux mises à jour actuelles
                </Button>
              </Box>
            )}
            {updates.length > 0 && !selectedUpdateFromHistory && (
              <>
                {updates.length > 1 && (
                  <Stepper activeStep={currentUpdateIndexInFullList >= 0 ? currentUpdateIndexInFullList : 0} alternativeLabel sx={{ mb: 3 }}>
                    {updates.map((update, index) => {
                      const isRead = readUpdates.has(update.id);
                      const isActive = index === currentUpdateIndexInFullList;
                      return (
                        <Step key={update.id} completed={isRead} active={isActive && !isRead}>
                          <StepLabel>
                            {dayjs(update.created_at).format('DD/MM/YYYY')}
                          </StepLabel>
                        </Step>
                      );
                    })}
                  </Stepper>
                )}
              </>
            )}

            <Box sx={{ maxHeight: '50vh', overflowY: 'auto', pr: 1 }}>
              {currentUpdate && (
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: (theme) => theme.shadows[2],
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      boxShadow: (theme) => theme.shadows[4],
                      transition: 'box-shadow 0.3s ease',
                    }
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {currentUpdate.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        backgroundColor: 'action.hover',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 500,
                      }}
                    >
                      {dayjs(currentUpdate.created_at).format('DD/MM/YYYY')}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.8,
                      color: 'text.primary',
                      fontSize: '0.95rem',
                    }}
                  >
                    {renderContent(currentUpdate.content)}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, backgroundColor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}`, gap: 2 }}>
        {!showHistoryList && !selectedUpdateFromHistory && (
          <Button
            onClick={handleShowHistory}
            variant="outlined"
            startIcon={<HistoryIcon />}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Voir une mise à jour antérieure
          </Button>
        )}
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2, px: 3 }}
        >
          Fermer
        </Button>
        {!hideMarkAsRead && !showHistoryList && !selectedUpdateFromHistory && (
          <Box display="flex" gap={2}>
            {currentUpdate && currentUpdateIndexInFullList < updates.length - 1 ? (
              <Button
                onClick={handleMarkCurrentAsRead}
                variant="contained"
                color="primary"
                sx={{ borderRadius: 2, px: 3 }}
                disabled={!currentUpdate || readUpdates.has(currentUpdate.id)}
              >
                Marquer comme lu et continuer
              </Button>
            ) : (
              <Button
                onClick={handleMarkCurrentAsRead}
                variant="contained"
                color="primary"
                sx={{ borderRadius: 2, px: 3 }}
                disabled={!currentUpdate || readUpdates.has(currentUpdate.id)}
              >
                Marquer comme lu et terminer
              </Button>
            )}
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalChangelog;

